import React, { useEffect, useState } from "react";
import { Edit, MoreHorizontal, Trash2, Power, Building2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";

/*
 * Mirrors the API facility (backendv2 FacilityResource / StoreFacilityRequest).
 * The prototype's `state`, `zip_code`, `email` and `coverage_areas` have no
 * column — the Saudi address model is `city` + `region` (FacilitySeeder), and the
 * type values are lowercase slugs, not display strings.
 */
const EMPTY = {
  name: "", name_ar: "", code: "", type: "hospital",
  address: "", city: "", region: "",
  phone: "", services: [],
  timezone: "Asia/Riyadh", status: "active",
};

export default function FacilitiesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);

  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try {
      const params = { page, per_page: pageSize };
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/facilities", { params });
      // Paginated resource collection: { data: [...], meta: { total, ... } }.
      setItems(res.data.data || []);
      setTotal(res.data.meta?.total ?? 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusFilter]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setErrors({});
    setDashboard(null);
    setSheetOpen(true);
  }
  async function openEdit(row) {
    setEditing(row);
    setForm({
      name: row.name, name_ar: row.name_ar || "", code: row.code, type: row.type || "hospital",
      address: row.address || "", city: row.city || "", region: row.region || "",
      phone: row.phone || "", services: row.services || [],
      timezone: row.timezone || "Asia/Riyadh", status: row.status || "active",
    });
    setErrors({});
    setSheetOpen(true);
    try {
      const d = await api.get(`/facilities/${row.id}/dashboard`);
      // { data: { facility, kpis } } — the KPIs sit one level in.
      setDashboard(d.data.data || null);
    } catch (_e) { setDashboard(null); }
  }

  function validate() {
    const e = {};
    if (!form.name) e.name = "Name is required";
    if (!form.code) e.code = "Code is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }
  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        name_ar: form.name_ar || null,
        type: form.type || null,
        address: form.address || null,
        city: form.city || null,
        region: form.region || null,
        phone: form.phone || null,
        timezone: form.timezone,
        services: form.services,
      };
      if (editing) {
        // `code` is immutable and `status` is not accepted here — status moves
        // through the activate / deactivate endpoints so each change is audited.
        await api.put(`/facilities/${editing.id}`, payload);
        toast.success("Facility updated");
      } else {
        await api.post("/facilities", { ...payload, code: form.code.trim(), status: form.status });
        toast.success("Facility created");
      }
      setSheetOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  async function doAction(row, action) {
    try {
      if (action === "delete") {
        await api.delete(`/facilities/${row.id}`);
        toast.success("Facility archived");
      } else {
        await api.post(`/facilities/${row.id}/${action}`);
        toast.success(`Facility ${action}d`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const columns = [
    {
      key: "name", label: "Facility",
      render: (r) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#EAF8FA] flex items-center justify-center text-[#0B7E8C]">
            <Building2 className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-[#1F2937] truncate">{r.name}</div>
            <div className="text-xs text-[#6B7280]">{r.code} · {r.type}</div>
          </div>
        </div>
      ),
    },
    { key: "city", label: "Location", render: (r) => [r.city, r.region].filter(Boolean).join(", ") || "—" },
    {
      key: "services", label: "Services", render: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.services || []).slice(0, 2).map((s) => (
            <Badge key={s} variant="outline" className="border-[#E5E7EB] text-[#6B7280] font-normal">{s}</Badge>
          ))}
          {(r.services || []).length > 2 && (
            <Badge variant="outline" className="border-[#E5E7EB] text-[#6B7280] font-normal">+{r.services.length - 2}</Badge>
          )}
        </div>
      ),
    },
    {
      key: "users_count", label: "Users",
      render: (r) => <span className="text-xs text-[#6B7280]">{r.users_count ?? 0} assigned</span>,
    },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "actions", label: "",
      render: (r) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button onClick={(e) => e.stopPropagation()} data-testid={`facility-actions-${r.id}`} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center">
              <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`facility-edit-${r.id}`}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {r.status !== "active" ? (
              <DropdownMenuItem onClick={() => doAction(r, "activate")}><Power className="w-4 h-4 mr-2" /> Activate</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => doAction(r, "deactivate")}><Power className="w-4 h-4 mr-2" /> Deactivate</DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-[#EF4444]"
              data-testid={`facility-delete-${r.id}`}
              onClick={() => setConfirm({ open: true, title: "Archive facility", description: `Archive ${r.name}?`, destructive: true, onConfirm: () => doAction(r, "delete") })}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="facilities-page">
      <PageHeader
        title="Facility Management"
        subtitle="H@H hubs, service configurations, and coverage areas."
        actions={
          <Button onClick={openCreate} data-testid="create-facility-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <Building2 className="w-4 h-4 mr-1.5" /> New Facility
          </Button>
        }
      />

      <DataTable
        testId="facilities-table"
        columns={columns} data={items} total={total} page={page} pageSize={pageSize}
        loading={loading} searchValue={search} onSearchChange={setSearch} onPageChange={setPage}
        exportFilename="facilities.csv"
        filters={
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="facility-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[680px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editing ? editing.name : "Create facility"}</SheetTitle>
            <SheetDescription>{editing ? `Code: ${editing.code}` : "Add a new H@H facility to the network."}</SheetDescription>
          </SheetHeader>

          <div className="p-6 space-y-4">
            {editing && dashboard?.kpis && (
              <div className="grid grid-cols-4 gap-3">
                {Object.entries(dashboard.kpis).map(([k, v]) => (
                  <div key={k} className="hah-card p-3">
                    <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">{k.replace(/_/g, " ")}</div>
                    {/* The patient KPIs are null until the patients module is wired
                        into this dashboard — render "—", never a misleading 0. */}
                    <div className="text-xl font-semibold text-[#1F2937] mt-1">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
            )}

            <FormSection title="General Information" testId="facility-form-general" description="Facility identity and contact.">
              <FormField label="Name" required error={errors.name}>
                <Input data-testid="facility-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormField>
              <FormField label="Code" required error={errors.code}>
                <Input data-testid="facility-code" disabled={!!editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </FormField>
              <FormField label="Name (Arabic)">
                <Input data-testid="facility-name-ar" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
              </FormField>
              <FormField label="Type">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="facility-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="clinic">Clinic</SelectItem>
                    <SelectItem value="hub">Hub</SelectItem>
                    <SelectItem value="command_center">Command Center</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              {editing ? (
                <FormField label="Status" hint="Changed from the row menu — Activate / Deactivate, so each change is audited.">
                  <Input disabled value={editing.status || "—"} data-testid="facility-status-readonly" />
                </FormField>
              ) : (
                <FormField label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="facility-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              )}
              <FormField label="Phone" hint="E.164, e.g. +966114889999"><Input data-testid="facility-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></FormField>
              <FormField label="Timezone"><Input data-testid="facility-timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></FormField>
              <FormField label="Address" full><Input data-testid="facility-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FormField>
              <FormField label="City"><Input data-testid="facility-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></FormField>
              <FormField label="Region"><Input data-testid="facility-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></FormField>
            </FormSection>

            <FormSection title="Service Configuration" testId="facility-form-services" description="Services offered.">
              <FormField label="Services (comma separated)" full hint="e.g., RPM, Acute Care at Home, Telehealth">
                <Input
                  data-testid="facility-services"
                  value={form.services.join(", ")}
                  onChange={(e) => setForm({ ...form, services: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                />
              </FormField>
            </FormSection>

            {editing && (
              <FormSection title="Audit Information" testId="facility-form-audit">
                <FormField label="Created">
                  <Input disabled value={editing.created_at ? new Date(editing.created_at).toLocaleString() : "—"} />
                </FormField>
                <FormField label="Last updated">
                  <Input disabled value={editing.updated_at ? new Date(editing.updated_at).toLocaleString() : "—"} />
                </FormField>
              </FormSection>
            )}
          </div>

          <StickyActionBar>
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="border-[#E5E7EB]" data-testid="facility-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="facility-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {saving ? "Saving…" : editing ? "Save changes" : "Create facility"}
            </Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}
